(function () {
  const root = document.querySelector('[data-predictive-search]');
  if (!root) return;

  const input = root.querySelector('#SearchInput');
  const panel = root.querySelector('#SearchResults');
  const token = panel.dataset.token;
  const form  = root.querySelector('form');

  // ---- CONFIG ----
  const STOREFRONT_API_URL = '/api/2025-10/graphql.json';
  const STOREFRONT_TOKEN   = token;
  const SUGGESTIONS_LIMIT  = 10;

  // --- Utils ---
  const debounce = (fn, ms = 200) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), ms);
    };
  };

  const escapeHTML = (s = '') =>
    String(s).replace(/[&<>"']/g, (m) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));

  const setExpanded = (bool) => input.setAttribute('aria-expanded', String(bool));
  const openPanel = () => { panel.hidden = false; setExpanded(true); };
  const closePanel = () => { panel.hidden = true; setExpanded(false); panel.innerHTML = ''; activeIndex = -1; };

  let activeIndex = -1;

  // Ensure a footer element exists to show "X Results" — we keep the same ID you referenced
  function ensureFooter() {
    let footer = panel.parentElement.querySelector('#search-bar-results-count');
    if (!footer) {
      footer = document.createElement('div');
      footer.id = 'search-bar-results-count';
      footer.style.display = 'none';
      footer.setAttribute('aria-hidden', 'true');
      panel.parentElement.appendChild(footer);
    }
    return footer;
  }

  // Build safe Shopify CDN/thumb URLs (returns 1x/2x)
  const shopifyThumb = (img, width = 80) => {
    if (!img) return { w1x: '', w2x: '' };
    const raw = typeof img === 'string' ? img : (img.url || '');
    if (!raw) return { w1x: '', w2x: '' };

    try {
      // Support protocol-relative and relative URLs
      const url1x = new URL(raw, window.location.origin);
      url1x.searchParams.set('width', width);
      const url2x = new URL(url1x.toString());
      url2x.searchParams.set('width', width * 2);
      return { w1x: url1x.toString(), w2x: url2x.toString() };
    } catch (e) {
      return { w1x: raw, w2x: raw };
    }
  };

  // Render helper for item HTML
  const makeItem = (href, title, img) => {
    const { w1x, w2x } = shopifyThumb(img, 80);
    const imgHTML = w1x
      ? `<img class="predictive-search__thumb"
               src="${w1x}"
               srcset="${w1x} 1x, ${w2x} 2x"
               sizes="80px"
               alt=""
               width="80"
               height="80"
               loading="lazy">`
      : '';

    return `
      <a class="predictive-search__item" role="option" href="${href}" data-title="${escapeHTML(title)}">
        ${imgHTML}
        <div class="predictive-search__title">${escapeHTML(title)}</div>
      </a>`;
  };

  // --- Footer updater (unified usage) ---
  function updateFooter(totalProducts, shown, query) {
    const footer = ensureFooter();
    if (!footer) return;

    if (totalProducts > 0) {
      const url = `/search?type=product&q=${encodeURIComponent(query)}`;
      const label = totalProducts === 1 ? 'Result' : 'Results';
      footer.innerHTML = `
        <a class="predictive-search__view-all" href="${url}">
          ${totalProducts} ${label}
        </a>`;
      footer.style.display = '';
      footer.removeAttribute('aria-hidden');
    } else {
      // hide/remove the footer if there is nothing extra to show
      footer.style.display = 'none';
      footer.setAttribute('aria-hidden', 'true');
      footer.innerHTML = '';
    }
  }

  // --- Render results from a normalized product array + totalCount ---
  function renderResultsFromProducts({ products = [], totalCount = 0 }, query) {
    if (!products.length) {
      panel.innerHTML = `<div class="predictive-search__empty" role="option" aria-selected="false">No results for “${escapeHTML(query)}”</div>`;
      openPanel();
      updateFooter(totalCount, 0, query);
      return;
    }

    const productHTML = products.map(p => makeItem(p.url, p.title, p.image)).join('');
    panel.innerHTML = `<div>${productHTML}</div>`;
    openPanel();

    updateFooter(totalCount, products.length, query);
  }

  // --- GraphQL-based suggestions (primary path) ---
  const fetchGqlSuggestions = debounce(async (q) => {
    if (!q || q.trim().length < 2) { closePanel(); return; }

    const GQL = `
      query Suggestions($q: String!, $first: Int!) {
        search(query: $q, types: [PRODUCT], first: $first) {
          totalCount
          nodes {
            ... on Product {
              title
              handle
              featuredImage {
                url
              }
            }
          }
        }
      }`;

    try {
      const res = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          query: GQL,
          variables: { q, first: SUGGESTIONS_LIMIT }
        })
      });

      if (!res.ok) throw new Error(`GraphQL network error: ${res.status}`);

      const json = await res.json();
      if (json.errors) {
        // Treat GraphQL errors as a failure so we fall back
        console.error('GraphQL errors:', json.errors);
        throw new Error('GraphQL errors');
      }

      const search = json?.data?.search;
      const totalCount = search?.totalCount ?? 0;
      const nodes = Array.isArray(search?.nodes) ? search.nodes : [];

      // Map nodes to same shape used by renderResultsFromProducts
      const products = nodes.map(node => ({
        title: node.title || '',
        url: node.handle ? `/products/${encodeURIComponent(node.handle)}` : '#',
        image: node.featuredImage?.url || ''
      }));

      renderResultsFromProducts({ products, totalCount }, q);
    } catch (err) {
      console.warn('GraphQL suggestions failed, falling back to /search/suggest.json', err);
      // fallback: use suggest.json (your original path) if GraphQL fails
      fetchSuggestFallback(q);
    }
  }, 200);

  // --- Fallback to /search/suggest.json (keeps backwards compatibility) ---
  async function fetchSuggestFallback(q) {
    const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product`;
    try {
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('Suggest network error');
      const data = await res.json();

      // Normalize the suggest.json shape to { products: [], totalCount: n }
      const products = ((data.resources && data.resources.results) || [])
        .map(p => ({
          title: p.title || '',
          url: p.url || (p.handle ? `/products/${encodeURIComponent(p.handle)}` : '#'),
          image: p.image || ''
        }));

      // suggest.json doesn't provide a totalCount; we conservatively set totalCount = products.length
      // to avoid showing a bogus "X Results" footer. Alternatively you could call your GraphQL totalCount separately.
      const totalCount = products.length;

      renderResultsFromProducts({ products, totalCount }, q);
    } catch (e) {
      console.error('Suggest fallback error:', e);
      panel.innerHTML = `<div class="predictive-search__error" role="option">Something went wrong. Press Enter to search.</div>`;
      openPanel();
    }
  }

  // --- Events ---
  input.addEventListener('input', (e) => {
    activeIndex = -1;
    fetchGqlSuggestions(e.target.value);
  });

  input.addEventListener('keydown', (e) => {
    if (panel.hidden) return;

    const items = Array.from(panel.querySelectorAll('[role="option"].predictive-search__item, .predictive-search__view-all'));
    if (!items.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        activeIndex = (activeIndex + 1) % items.length;
        updateActive(items);
        break;
      case 'ArrowUp':
        e.preventDefault();
        activeIndex = (activeIndex - 1 + items.length) % items.length;
        updateActive(items);
        break;
      case 'Enter':
        if (activeIndex > -1) {
          e.preventDefault();
          items[activeIndex].click();
        }
        break;
      case 'Escape':
        closePanel();
        break;
    }
  });

  document.addEventListener('click', (e) => {
    if (!root.contains(e.target)) closePanel();
  });

  form.addEventListener('submit', () => closePanel());

  function updateActive(items) {
    items.forEach((el, i) => {
      if (i === activeIndex) {
        el.classList.add('is-active');
        el.setAttribute('aria-selected', 'true');
        el.scrollIntoView({ block: 'nearest' });
      } else {
        el.classList.remove('is-active');
        el.setAttribute('aria-selected', 'false');
      }
    });
  }

  // Open/close search UI buttons (keep your previous behavior)
  const openButtons = document.querySelectorAll('.open-search');
  const closeButtons = document.querySelectorAll('.close-search');
  const searchBar = document.getElementById('search-bar');

  openButtons.forEach(button => {
    button.addEventListener('click', function(event) {
      event.preventDefault();
      if (searchBar) searchBar.classList.add('open');
      const inputEl = searchBar ? searchBar.querySelector('input') : null;
      if (inputEl) {
        inputEl.focus();
        const len = inputEl.value.length;
        inputEl.setSelectionRange(len, len);
      }
    });
  });

  closeButtons.forEach(button => {
    button.addEventListener('click', function(event) {
      event.preventDefault();
      if (searchBar) searchBar.classList.remove('open');
    });
  });

})();